import {
    faCheckCircle,
    faLink,
    faShieldAlt,
    faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Badge, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Banderas from '../cliente/Banderas';
import ModalSeguimientoLead from '../leads/ModalSeguimientoLead';

/**
 * Componente para mostrar una licencia individual como tarjeta
 */
const LicenciaCard = ({ licencia, isSelected = false, onToggleSelection }) => {
    const url = `/licencias/detalle/${licencia.idLicencia}`;
    const urlCliente = `/clientes/detalle/${licencia.idClienteAkasia}`;
    const [showModalSeguimientoLead, setShowModalSeguimientoLead] = useState(false);
    const [idLeadModal, setIdLeadModal] = useState(0);

    // Función para calcular días restantes de la póliza
    const calcularDiasRestantesPoliza = (fechaVencimiento) => {
        if (!fechaVencimiento) return null;
        
        try {
            const fechaVenc = new Date(fechaVencimiento);
            const fechaActual = new Date();
            fechaActual.setHours(0, 0, 0, 0); 
            fechaVenc.setHours(0, 0, 0, 0);
            
            const diferenciaTiempo = fechaVenc.getTime() - fechaActual.getTime();
            const diferenciaDias = Math.ceil(diferenciaTiempo / (1000 * 3600 * 24));
            
            return diferenciaDias;
        } catch (error) {
            return null;
        }
    };

    // Obtener días restantes de la póliza
    const diasRestantesPoliza = licencia.dDiasFaltantesPoliza !== undefined 
        ? licencia.dDiasFaltantesPoliza 
        : calcularDiasRestantesPoliza(licencia.dFechaVencimientoSoporteBD);

    const handleCardClick = (e) => {
        // No activar selección si se hace clic en un botón o enlace
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }

        if (onToggleSelection) {
            onToggleSelection(licencia.idLicencia);
        }
    };

    // Determinar clases CSS según estado
    const cardClasses = [
        'mb-3',
        'shadow-sm',
        licencia.bIsPeriodoPrueba ? 'border-danger' : '',
        licencia.bCancelado ? 'border-warning' : '',
        isSelected ? 'border-primary border-3' : ''
    ].filter(Boolean).join(' ');

    // Estilos dinámicos para el fondo compatible con modo oscuro
    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ...(isSelected && {
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        })
    };

    return (
        <Card
            className={cardClasses}
            onClick={handleCardClick}
            style={cardStyle}
        >
            <Card.Body>
                    <>
                        <Row className="align-items-center">
                            {/* ID, Tipo y Estado */}
                            <Col xs={12} md={2} className="mb-2 mb-md-0">
                                <div className="d-flex flex-column align-items-start">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        {isSelected && (
                                            <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className="text-primary"
                                                size="lg"
                                            />
                                        )}
                                        <Link to={url} className="text-decoration-none">
                                            <span className={`fw-bold ${licencia.bIsPeriodoPrueba ? 'text-danger' : ''}`} style={{ fontSize: '1.1rem', color: licencia.bIsPeriodoPrueba ? '' : '#2c7be5' }}>
                                                #{licencia.idLicencia}
                                            </span>
                                        </Link>
                                    </div>

                                    {/* Tipo de Licencia */}
                                    <div className="mb-2">
                                        <img 
                                            src={licencia.idCatTipoLicencia === 1 ? '/img/servidor48.png' : licencia.idCatTipoLicencia === 2 ? '/img/caja48.png' : licencia.idCatTipoLicencia === 3 ? '/img/nube48.png' : licencia.idCatTipoLicencia === 7 ? '/img/akasiapos48.png' : ''} 
                                            alt={licencia.vDescripcionTipo}
                                            width="48"
                                        />
                                        <div className="small text-muted mt-1">
                                            <strong>{licencia.vDescripcionTipo}</strong>
                                        </div>
                                    </div>

                                    {/* Estados */}
                                    <div className="d-flex gap-1 mb-2">
                                        {licencia.bCancelado ? (
                                            <Badge bg="danger" className="fs--2">CANCELADO</Badge>
                                        ) : (
                                            <>
                                                {licencia.bIsPeriodoPrueba ? (
                                                    <Badge bg="danger" className="fs--2">PRUEBA</Badge>
                                                ) : (
                                                    <Badge bg="success" className="fs--2">ACTIVA</Badge>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Iconos de estado adicionales */}
                                    <div className="d-flex gap-2">
                                        {/* Nube Akasia */}
                                        {licencia.idCatTipoLicencia === 3 && (
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip>
                                                        <strong>NUBE AKASIA</strong><br />
                                                        Vencimiento: {licencia.dFinLicenciaUso}<br />
                                                        Restante: {licencia.dDiasFaltantes} días
                                                    </Tooltip>
                                                }
                                            >
                                                {licencia.dDiasFaltantes > 0 ? (
                                                    <img src="/img/nubeActiva.png" alt="Nube Activa" width="24" />
                                                ) : (
                                                    <img src="/img/nubeVencida.png" alt="Nube Vencida" width="24" />
                                                )}
                                            </OverlayTrigger>
                                        )}

                                        {/* Póliza de Soporte - Estado 1: Activa */}
                                        {licencia.iEstadoPoliza === 1 && (
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip>
                                                        <strong>PÓLIZA SOPORTE TÉCNICO</strong><br />
                                                        Vencimiento: {licencia.dFechaVencimientoSoporte}<br />
                                                        {diasRestantesPoliza !== null && (
                                                            <>
                                                                {diasRestantesPoliza > 0 ? (
                                                                    <span className="text-success fw-bold">
                                                                        Restan {diasRestantesPoliza} día{diasRestantesPoliza !== 1 ? 's' : ''}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-danger fw-bold">VENCIDA</span>
                                                                )}
                                                            </>
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <img src="/img/polizaActiva.png" alt="Póliza Activa" width="24" />
                                            </OverlayTrigger>
                                        )}

                                        {/* Póliza de Soporte - Estado 2: Vencida */}
                                        {licencia.iEstadoPoliza === 2 && (
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip>
                                                        <strong>PÓLIZA SOPORTE TÉCNICO</strong><br />
                                                        Vencimiento: {licencia.dFechaVencimientoSoporte}<br />
                                                        <span className="text-danger fw-bold">VENCIDA</span>
                                                    </Tooltip>
                                                }
                                            >
                                                <img src="/img/polizaVencida.png" alt="Póliza Vencida" width="24" />
                                            </OverlayTrigger>
                                        )}

                                        {/* Póliza de Soporte - Estados 3, 4, 5: Por vencer */}
                                        {(licencia.iEstadoPoliza === 3 || licencia.iEstadoPoliza === 4 || licencia.iEstadoPoliza === 5) && (
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip>
                                                        <strong>PÓLIZA SOPORTE TÉCNICO</strong><br />
                                                        Vencimiento: {licencia.dFechaVencimientoSoporte}<br />
                                                        {diasRestantesPoliza !== null && (
                                                            <>
                                                                {diasRestantesPoliza > 0 ? (
                                                                    <span className="text-warning fw-bold">
                                                                        ⚠️ Restan {diasRestantesPoliza} día{diasRestantesPoliza !== 1 ? 's' : ''}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-danger fw-bold">VENCIDA</span>
                                                                )}
                                                            </>
                                                        )}
                                                    </Tooltip>
                                                }
                                            >
                                                <img src="/img/polizaPorVencer.png" alt="Póliza Por Vencer" width="24" />
                                            </OverlayTrigger>
                                        )}

                                        {/* PC Autorizada */}
                                        {licencia.vDatosPCAutorizada && (
                                            <OverlayTrigger
                                                overlay={
                                                    <Tooltip>
                                                        <strong>PC AUTORIZADA</strong><br />
                                                        {licencia.vDatosPCAutorizada}
                                                    </Tooltip>
                                                }
                                            >
                                                <img src="/img/pcautorizada25.png" alt="PC Autorizada" width="24" />
                                            </OverlayTrigger>
                                        )}
                                    </div>

                                    <small className="text-muted mt-2">
                                        Última conexión:<br />
                                        {licencia.dUltimaConexion}
                                    </small>
                                </div>
                            </Col>

                            {/* Información de Empresa y Cliente */}
                            <Col xs={12} md={6} className="mb-2 mb-md-0">
                                <Row>
                                    {/* Columna 1: Datos de la Empresa */}
                                    <Col xs={12} lg={6} className="mb-2 mb-lg-0">
                                        <div className="mb-2">
                                            <div className="text-muted small mb-2">
                                                <strong className="text-primary">Empresa</strong>
                                            </div>

                                            <h6 className={`mb-2 ${licencia.bIsPeriodoPrueba ? 'text-danger' : ''}`}>
                                                <Link to={url} className="text-decoration-none">
                                                    <span className="d-inline-block text-truncate" style={{ maxWidth: '250px' }}>
                                                        {licencia.vNombreEmpresa}
                                                    </span>
                                                </Link>
                                            </h6>

                                            {/* País con Bandera */}
                                            {licencia.vPais && (
                                                <div className="small text-muted mb-2 d-flex align-items-center">
                                                    <strong className="me-2">País:</strong>
                                                    <Banderas Height={20} vPais={licencia.vPais} />
                                                    <span className="ms-2">{licencia.vPais}</span>
                                                </div>
                                            )}

                                            {/* Giro Comercial */}
                                            {licencia.vGiroComercial && (
                                                <div className="small text-muted mb-2">
                                                    <strong>Giro:</strong> {licencia.vGiroComercial}
                                                </div>
                                            )}

                                            {/* Módulos */}
                                            {licencia.vModulos && (
                                                <div className="small text-muted mb-2">
                                                    <strong>Módulos:</strong>
                                                    <div className="text-truncate" style={{ maxWidth: '250px' }} title={licencia.vModulos}>
                                                        {licencia.vModulos}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Coordenadas */}
                                            {licencia.vLatLon && (
                                                <div className="small text-muted mb-1">
                                                    <strong>Ubicación:</strong> 
                                                    <a 
                                                        href={`https://www.google.com/maps?q=${licencia.vLatLon}`} 
                                                        target="_blank" 
                                                        rel="noreferrer" 
                                                        className="text-decoration-none ms-1"
                                                        title="Ver en Google Maps"
                                                    >
                                                        <FontAwesomeIcon icon={faGlobe} className="me-1 text-primary" />
                                                        {licencia.vLatLon}
                                                    </a>
                                                </div>
                                            )}

                                            {/* Información de cancelación */}
                                            {licencia.bCancelado && (
                                                <>
                                                    <hr className="my-2" />
                                                    <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '0.85rem' }}>
                                                        <div className="fw-bold mb-1">
                                                            ⚠️ LICENCIA CANCELADA
                                                        </div>
                                                        {licencia.vMotivoCancelacion && (
                                                            <div className="small mb-1">
                                                                <strong>Motivo:</strong>
                                                                <div className="mt-1">{licencia.vMotivoCancelacion}</div>
                                                            </div>
                                                        )}
                                                        {licencia.vUsuarioCancelador && (
                                                            <div className="small">
                                                                <strong>Cancelado por:</strong> {licencia.vUsuarioCancelador}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <hr className="my-2" />
                                                </>
                                            )}

                                            {/* Vendedor */}
                                            {licencia.vNombreVendedor && (
                                                <div className="small text-muted mt-2">
                                                    <strong>Vendedor:</strong> {licencia.vNombreVendedor}
                                                </div>
                                            )}

                                            {/* Lead con etapa*/}
                                            {licencia.idLead > 0 && (
                                                <div className="small text-muted mt-1">
                                                    <FontAwesomeIcon icon={faLink} className="me-1 text-info" />
                                                    <Link 
                                                        to="#" 
                                                        className="text-decoration-none"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setIdLeadModal(licencia.idLead);
                                                            setShowModalSeguimientoLead(true);
                                                        }}
                                                    >
                                                        Lead #{licencia.idLead}
                                                    </Link>
                                                    {licencia.vNombreEtapa && (
                                                        <span className="ms-2">
                                                            <Badge bg="info" className="fs--2">
                                                                {licencia.vNombreEtapa}
                                                            </Badge>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </Col>

                                    {/* Columna 2: Datos del Cliente */}
                                    <Col xs={12} lg={6}>
                                        <div className="mb-2">
                                            <div className="text-muted small mb-2">
                                                <strong className="text-info">Cliente</strong>
                                            </div>

                                            <h6 className={`mb-2 ${licencia.bIsPeriodoPrueba ? 'text-danger' : ''}`}>
                                                <Link to={urlCliente} className="text-decoration-none">
                                                    <span className="d-inline-block text-truncate" style={{ maxWidth: '250px' }}>
                                                        {licencia.vNombreCliente}
                                                    </span>
                                                </Link>
                                            </h6>

                                            {/* Email */}
                                            {licencia.vEmail && (
                                                <div className="small mb-2 text-muted">
                                                    <strong>Email:</strong>
                                                    <div>
                                                        <a href={`mailto:${licencia.vEmail}`} className="text-decoration-none">
                                                            <span className="d-inline-block text-truncate" style={{ maxWidth: '220px' }}>
                                                                📧 {licencia.vEmail}
                                                            </span>
                                                        </a>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Teléfono Personal */}
                                            {licencia.vTelefonoPersonal && (
                                                <div className="small mb-2 text-muted">
                                                    <strong>Teléfono Personal:</strong>
                                                    <div>
                                                        <a href={`https://wa.me/${licencia.vTelefonoPersonal}`} target="_blank" rel="noreferrer" className="text-decoration-none">
                                                            <img src="/img/whatsapp20.png" alt="WhatsApp" className="me-1" />
                                                            {licencia.vTelefonoPersonal}
                                                        </a>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Teléfono Trabajo */}
                                            {licencia.vTelefonoTrabajo && (
                                                <div className="small text-muted mb-2">
                                                    <strong>Teléfono Trabajo:</strong>
                                                    <div>
                                                        📞 {licencia.vTelefonoTrabajo}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Línea divisora */}
                                            <hr className="my-2" />

                                            {/* Datos de Facturación */}
                                            <div className="text-muted small mb-2">
                                                <strong className="text-warning">Datos de Facturación</strong>
                                            </div>

                                            {/* RFC */}
                                            {licencia.vRfc && (
                                                <div className="small text-muted mb-1">
                                                    <strong>RFC:</strong> {licencia.vRfc}
                                                </div>
                                            )}

                                            {/* Razón Social */}
                                            {licencia.vRazonSocial && (
                                                <div className="small text-muted mb-1">
                                                    <strong>Razón Social:</strong>
                                                    <div className="text-truncate" style={{ maxWidth: '220px' }}>
                                                        {licencia.vRazonSocial}
                                                    </div>
                                                </div>
                                            )}

                                            {/* País de Facturación */}
                                            {licencia.vPaisFacturacion && (
                                                <div className="small text-muted mb-1">
                                                    <strong>País Facturación:</strong> {licencia.vPaisFacturacion}
                                                </div>
                                            )}

                                            {/* Número de Registro Tributario */}
                                            {licencia.vNumRegTrib && (
                                                <div className="small text-muted mb-1">
                                                    <strong>Núm. Reg. Trib.:</strong> {licencia.vNumRegTrib}
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Información adicional */}
                            <Col xs={12} md={4} className="text-md-end">
                                <div className="mb-3">
                                    {/* UUID Empresa */}
                                    <div className="small mb-2">
                                        <div className="text-muted mb-1">
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-1 text-info" />
                                            <strong>UUID Empresa</strong>
                                        </div>
                                        <div className="text-end">
                                            <b>{licencia.vUUIDEmpresa}</b>
                                        </div>
                                    </div>

                                    {/* UUID Licencia */}
                                    <div className="small mb-2">
                                        <div className="text-muted mb-1">
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-1 text-success" />
                                            <strong>UUID Licencia</strong>
                                        </div>
                                        <div className="text-end">
                                            <b>{licencia.vUUIDLicencia}</b>
                                        </div>
                                    </div>

                                    {/* Fin Licencia de Uso */}
                                    {licencia.dFinLicenciaUso && (
                                        <div className="small mb-3">
                                            <div className="text-muted mb-1">
                                                <strong>Vencimiento</strong>
                                            </div>
                                            <div className="text-danger fw-bold" style={{ fontSize: '0.9rem' }}>
                                                {licencia.dFinLicenciaUso}
                                            </div>
                                            {licencia.dDiasFaltantes !== undefined && licencia.dDiasFaltantes !== null && (
                                                <div className={`mt-1 fw-bold ${
                                                    licencia.dDiasFaltantes > 1825 ? 'text-info' : 
                                                    licencia.dDiasFaltantes <= 0 ? 'text-danger' : 
                                                    licencia.dDiasFaltantes <= 30 ? 'text-warning' : 
                                                    'text-success'
                                                }`} style={{ fontSize: '0.85rem' }}>
                                                    {licencia.dDiasFaltantes > 1825 ? (
                                                        <>♾️ PERMANENTE</>
                                                    ) : licencia.dDiasFaltantes <= 0 ? (
                                                        <>⚠️ Vencida</>
                                                    ) : licencia.dDiasFaltantes === 1 ? (
                                                        <>⏰ {licencia.dDiasFaltantes} día restante</>
                                                    ) : (
                                                        <>⏰ {licencia.dDiasFaltantes} días restantes</>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <hr className="my-2" />
                                </div>

                                {/* Ver detalles link y botón para contraer */}
                                <div className="mt-3 d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation();  }}
                                    >
                                        Cerrar
                                    </button>
                                    <Link to={url} className="btn btn-sm btn-outline-primary">
                                        <img src="/img/ver20.png" alt="Ver" className="me-1" />
                                        Ver detalles
                                    </Link>
                                </div>
                            </Col>
                        </Row>
                    </>
           
            </Card.Body>
            {/* Modal de Seguimiento de Lead */}
            {showModalSeguimientoLead && (
                <ModalSeguimientoLead
                    showModal={showModalSeguimientoLead}
                    setShowModal={setShowModalSeguimientoLead}
                    idLead={idLeadModal}
                    setIdLead={setIdLeadModal}
                />
            )}
        </Card>
    );
};

LicenciaCard.propTypes = {
    licencia: PropTypes.shape({
        idLicencia: PropTypes.number.isRequired,
        idClienteAkasia: PropTypes.number.isRequired,
        vNombreEmpresa: PropTypes.string.isRequired,
        vNombreCliente: PropTypes.string.isRequired,
        vDescripcionTipo: PropTypes.string.isRequired,
        idCatTipoLicencia: PropTypes.number.isRequired,
        bIsPeriodoPrueba: PropTypes.bool,
        bCancelado: PropTypes.bool,
        vMotivoCancelacion: PropTypes.string,
        vUsuarioCancelador: PropTypes.string,
        dUltimaConexion: PropTypes.string,
        vUUIDEmpresa: PropTypes.string,
        vUUIDLicencia: PropTypes.string,
        vPais: PropTypes.string,
        vGiroComercial: PropTypes.string,
        vModulos: PropTypes.string,
        dFinLicenciaUso: PropTypes.string,
        vLatLon: PropTypes.string,
        vNombreVendedor: PropTypes.string,
        vEmail: PropTypes.string,
        vTelefonoPersonal: PropTypes.string,
        vTelefonoTrabajo: PropTypes.string,
        vRfc: PropTypes.string,
        vRazonSocial: PropTypes.string,
        vPaisFacturacion: PropTypes.string,
        vNumRegTrib: PropTypes.string,
        iEstadoPoliza: PropTypes.number,
        dFechaVencimientoSoporte: PropTypes.string,
        dFechaVencimientoSoporteBD: PropTypes.string,
        dDiasFaltantes: PropTypes.number,
        dDiasFaltantesPoliza: PropTypes.number,
        vDatosPCAutorizada: PropTypes.string,
        idLead: PropTypes.number,
        vNombreEtapa: PropTypes.string
    }).isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default LicenciaCard;
